import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { UserCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

/**
 * Chantier 3 C10 (2026-09-06) — Badge "Assigné à: {name}" pour liste/détail.
 *
 * Read-only : affiche le nom du commercial responsable. Cache un fetch
 * `/team/members` en mémoire de module pour éviter N requêtes dans une liste.
 *
 * Props:
 *   - userId: string | null  →  si null, affiche "Non assigné" (badge grisé)
 *   - variant: "inline" | "badge" (défaut "badge")
 */

let _membersCache = null;
let _membersPromise = null;

async function getMembers() {
  if (_membersCache) return _membersCache;
  if (_membersPromise) return _membersPromise;
  _membersPromise = api
    .get("/team/members")
    .then(({ data }) => {
      const arr = Array.isArray(data) ? data : data?.items || [];
      _membersCache = arr;
      return arr;
    })
    .catch(() => {
      _membersCache = [];
      return [];
    })
    .finally(() => {
      _membersPromise = null;
    });
  return _membersPromise;
}

export function invalidateAssignedMembersCache() {
  _membersCache = null;
  _membersPromise = null;
}

export default function QuoteAssignedBadge({ userId, variant = "badge" }) {
  const [name, setName] = useState(null); // null = loading, "" = non trouvé, "X Y" = ok

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setName("");
      return;
    }
    getMembers().then((arr) => {
      if (cancelled) return;
      const m = arr.find((x) => x.id === userId);
      setName(m ? `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email : "");
    });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userId) {
    return (
      <Badge
        variant="outline"
        className="border-slate-700/60 bg-slate-800/40 text-slate-500 font-normal"
        data-testid="quote-assigned-badge-unassigned"
      >
        <UserCircle2 className="mr-1 h-3 w-3" aria-hidden />
        Non assigné
      </Badge>
    );
  }

  const label = name === null ? "…" : name || "Utilisateur inconnu";

  if (variant === "inline") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-slate-400"
        data-testid={`quote-assigned-inline-${userId}`}
      >
        <UserCircle2 className="h-3.5 w-3.5" aria-hidden />
        {label}
      </span>
    );
  }

  return (
    <Badge
      variant="outline"
      className="border-slate-600/60 bg-slate-800/40 text-slate-300 font-normal"
      data-testid={`quote-assigned-badge-${userId}`}
    >
      <UserCircle2 className="mr-1 h-3 w-3" aria-hidden />
      {label}
    </Badge>
  );
}
